import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {format} from 'date-fns';
import {Option, OptionGroup} from '@shared/models/option';
import {PopoverMenuComponent} from '@lib/components/popover-menu/popover-menu.component';
import {DatetimePickerComponent} from '@lib/components/datetime-picker/datetime-picker.component';
import {CustomStepPickerComponent} from '@lib/components/custom-step-picker/custom-step-picker.component';
import {NavigationDirection} from '@core/services/tile-history-navigation.helper';
import {CUSTOM_STEP_ID, DATETIME_MODE_LABELS, DEFAULT_TIME_STEP, DateTimeMode, TIME_STEPS, TIME_UNIT_MULTIPLIERS, TIME_UNIT_SUFFIX, TimeStep, TimeUnit} from '@features/canvas-tiles/shared/canvas-tile.constants';

@Component({
  selector: 'motn-canvas-replay-controls',
  standalone: true,
  templateUrl: './canvas-replay-controls.component.html',
  styleUrls: ['./canvas-replay-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PopoverMenuComponent, DatetimePickerComponent, CustomStepPickerComponent],
})
export class CanvasReplayControlsComponent {
  @Input() loading = false;

  @Output() replayTimestampChange = new EventEmitter<number | null>();

  readonly popoverPositions: ConnectedPosition[] = [{
    originX: 'center',
    originY: 'center',
    overlayX: 'center',
    overlayY: 'center',
  }];

  selectedDatetime = new Date();
  dateTimeMode = DateTimeMode.Latest;
  dateTimeMenuOpen = false;
  datePickerOpen = false;

  selectedTimeStep: TimeStep = DEFAULT_TIME_STEP;
  stepSelectorOpen = false;
  customStepDialogOpen = false;
  customStepValue = 1;
  customStepUnit = TimeUnit.MINUTES;

  get dateTimeOptions(): OptionGroup {
    return new OptionGroup([
      {
        id: DateTimeMode.Latest,
        name: DATETIME_MODE_LABELS[DateTimeMode.Latest],
        callback: () => this.resumeLive(),
        selected: this.dateTimeMode === DateTimeMode.Latest,
      },
      {
        id: DateTimeMode.Custom,
        name: DATETIME_MODE_LABELS[DateTimeMode.Custom],
        callback: () => {
          this.dateTimeMode = DateTimeMode.Custom;
          this.dateTimeMenuOpen = false;
          this.datePickerOpen = true;
        },
        selected: this.dateTimeMode === DateTimeMode.Custom,
      },
    ]);
  }

  get stepOptions(): OptionGroup {
    return new OptionGroup(
      TIME_STEPS.map(step => ({
        id: step.id,
        name: step.label,
        selected: step.id === this.selectedTimeStep.id,
      }))
    );
  }

  get dateTimePillText(): string {
    if (this.dateTimeMode === DateTimeMode.Latest) {
      return DATETIME_MODE_LABELS[DateTimeMode.Latest];
    }
    return format(this.selectedDatetime, 'MMM d, yyyy h:mm a');
  }

  get stepPillText(): string {
    if (this.selectedTimeStep.id === CUSTOM_STEP_ID) {
      return `Step: ${this.customStepValue}${TIME_UNIT_SUFFIX[this.customStepUnit]}`;
    }
    return `Step: ${this.selectedTimeStep.id}`;
  }

  get isAtLatest(): boolean {
    if (this.dateTimeMode === DateTimeMode.Latest) return true;
    return this.selectedDatetime.getTime() >= Date.now();
  }

  onDateTimeChange(date: Date): void {
    this.selectedDatetime = date;
    this.dateTimeMode = DateTimeMode.Custom;
    this.replayTimestampChange.emit(date.getTime());
  }

  onStepChange(option: Option): void {
    const step = TIME_STEPS.find(s => s.id === option.id);
    if (!step) return;

    this.stepSelectorOpen = false;
    if (step.id === CUSTOM_STEP_ID) {
      this.customStepDialogOpen = true;
    } else {
      this.selectedTimeStep = step;
    }
  }

  onCustomStepChange(timeStep: TimeStep): void {
    this.selectedTimeStep = timeStep;
    this.customStepValue = Math.round(timeStep.seconds / TIME_UNIT_MULTIPLIERS[this.customStepUnit]);
  }

  navigateOlder(): void {
    this.navigateByStep(NavigationDirection.OLDER);
  }

  navigateNewer(): void {
    if (this.isAtLatest) return;
    this.navigateByStep(NavigationDirection.NEWER);
  }

  private resumeLive(): void {
    this.dateTimeMode = DateTimeMode.Latest;
    this.selectedDatetime = new Date();
    this.dateTimeMenuOpen = false;
    this.replayTimestampChange.emit(null);
  }

  private navigateByStep(direction: NavigationDirection): void {
    if (this.loading) return;

    const stepSeconds = this.selectedTimeStep?.seconds ?? 0;
    if (stepSeconds === 0) {
      console.warn('Cannot navigate: no time step selected');
      return;
    }

    if (this.dateTimeMode === DateTimeMode.Latest) {
      this.dateTimeMode = DateTimeMode.Custom;
      this.selectedDatetime = new Date();
    }

    const timestamp = Math.min(
      this.selectedDatetime.getTime() + direction * stepSeconds * 1000,
      direction === NavigationDirection.NEWER ? Date.now() : Infinity,
    );
    this.selectedDatetime = new Date(timestamp);
    this.replayTimestampChange.emit(timestamp);
  }
}
